import { CameraControls2 } from '@Components/BigBrowse/CanvasManager/CameraControls/CameraControls2';
import { CenterIndicator } from '@Components/BigBrowse/bb2/CenterIndicator';
import { DesktopZoomPanel } from '@Components/BigBrowse/bb2/DesktopZoomPanel/DesktopZoomPanel';
import { ScrollBoundaryHelper } from '@Components/BigBrowse/bb2/ScrollBoundaryHelper';
import { CameraBoundaryHelper } from '@Components/BigBrowse/bb2/meshes2/CameraBoundaryHelper';
import { FloorMap } from '@Map/FloorMap/FloorMap';
import { MiniMap2 } from '@Map/MiniMap2/MiniMap2';
import { animated, useSpring } from '@react-spring/native';
import { Canvas } from '@react-three/fiber';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type {
  GestureEvent,
  PanGestureHandlerEventPayload,
} from 'react-native-gesture-handler';
import { PanGestureHandler, State } from 'react-native-gesture-handler';
import styled from 'styled-components/native';

import ShopFloorWelcomeBlurb from '../ShopFloorWelcomeBlurb/ShopFloorWelcomeBlurb';
import BBToolbar from '../Views/DetailViews/BBToolbar/BBToolbar';
import {
  BbContentPresenterContext,
  getZoomSpanForZoom,
  useBbContentPresenter,
} from './BbContentPresenter';
import { GridCardMesh2 } from './meshes2/GridCardMesh2';

import {
  DISPLAY_CAMERA_BOUNDARY_HELPER,
  DISPLAY_CAMERA_HELPER,
  DISPLAY_CANVAS_VERTICAL_LIMITS,
  DISPLAY_FOCUS_CROSS,
  DISPLAY_SCROLL_BOUNDARY_HELPER,
} from '~config/canvas-helpers';

export const StyledMiniMapWrapper = styled.View`
  position: absolute;
  left: 0;
  bottom: 0;
`;

export const BbScreenContent = observer(function BbScreenContent() {
  const contentPresenter = useBbContentPresenter();

  const {
    canvasManager,
    mountItems,
    onCardClick,
    showWelcomeMessage,
    isInitializing,
    showBottomNav,
    visible: { value: visible },
    fetchError,
    isDesktopZoomEnabled,
    currentZoomSpan,
    enableSuppressHeroBackgroundFX,
    enableCollapsedDisplaysFX,
  } = contentPresenter;
  const { windowSize, cameraSpecs, cameraPosition, handlePan } = canvasManager;

  const { opacity } = useSpring({
    to: { opacity: Number(visible) },
    config: { duration: 500 },
  });

  const onPanEvent = ({
    nativeEvent,
  }: GestureEvent<PanGestureHandlerEventPayload>) => {
    if (nativeEvent.state === State.END) {
      handlePan(nativeEvent.velocityX, nativeEvent.velocityY);
    }
  };

  return (
    <animated.View
      style={[styles.wrapper, { opacity }]}
      onLayout={({
        nativeEvent: {
          layout: { width, height },
        },
      }) => {
        windowSize.set({ width, height });
      }}
    >
      {Boolean(fetchError) && (
        <>
          <Text style={styles.error}>Oops!</Text>
          <Text style={styles.error}>{fetchError}</Text>
          <Text style={styles.error}>:-/</Text>
        </>
      )}
      {!fetchError && (
        <PanGestureHandler onGestureEvent={onPanEvent}>
          <Canvas orthographic camera={cameraSpecs} flat style={styles.canvas}>
            {/* Context is not passed-through Canvas, so we need to re-provide it
        
        https://github.com/pmndrs/react-three-fiber/issues/262
         */}
            <BbContentPresenterContext.Provider value={contentPresenter}>
              <CameraControls2 canvasManager={canvasManager} />
              {DISPLAY_CAMERA_BOUNDARY_HELPER && <CameraBoundaryHelper />}
              {DISPLAY_SCROLL_BOUNDARY_HELPER && <ScrollBoundaryHelper />}

              {mountItems.map((item) => (
                <GridCardMesh2
                  key={item.key}
                  item={item}
                  onClick={() => onCardClick(item)}
                />
              ))}

              {DISPLAY_CANVAS_VERTICAL_LIMITS && (
                <>
                  <mesh position={[0, windowSize.value.height / 2, 0.1]}>
                    <circleGeometry args={[5]} />
                    <meshBasicMaterial color='red' />
                  </mesh>

                  <mesh position={[0, 0, 0.1]}>
                    <circleGeometry args={[5]} />
                    <meshBasicMaterial color='green' />
                  </mesh>

                  <mesh position={[0, -windowSize.value.height / 2, 0.1]}>
                    <circleGeometry args={[5]} />
                    <meshBasicMaterial color='red' />
                  </mesh>
                </>
              )}
            </BbContentPresenterContext.Provider>
          </Canvas>
        </PanGestureHandler>
      )}
      {isDesktopZoomEnabled && <DesktopZoomPanel />}
      <ShopFloorWelcomeBlurb
        showWelcomeBlurb={showWelcomeMessage}
        isInitializing={isInitializing}
      />
      {showBottomNav && <BBToolbar />}
      <StyledMiniMapWrapper>
        <MiniMap2 />
      </StyledMiniMapWrapper>
      <FloorMap />
      {DISPLAY_CAMERA_HELPER && (
        <View style={styles.cameraHelper}>
          <Text>
            Position: {cameraPosition.x.toFixed(0)}{' '}
            {cameraPosition.y.toFixed(0)}
          </Text>
          <Text>
            Size: {windowSize.value.width} x {windowSize.value.height}
          </Text>
          <Text>
            Zoom Span: {currentZoomSpan.value} {canvasManager.cameraZoom.value}
          </Text>
          <Text>
            FX: Collapse {String(enableCollapsedDisplaysFX.value)} - NoBack:{' '}
            {String(enableSuppressHeroBackgroundFX.value)}
          </Text>
        </View>
      )}
      {DISPLAY_FOCUS_CROSS && <CenterIndicator />}
    </animated.View>
  );
});

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  canvas: {
    flex: 1,
    width: '100%',
  },
  cameraHelper: {
    position: 'absolute',
    top: 70,
    left: 0,
    backgroundColor: 'yellow',
  },
  error: {
    fontSize: 26,
  },
});
